import React, { useMemo } from 'react';

import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { ConfirmEntry, ConfirmStat, ConfirmSummary } from '~/types/availability';

interface Props {
  open: boolean;
  onBack: () => void;
  onConfirm: () => void;
  summary: ConfirmSummary;
  title: string;
  description: string;
  isApplying?: boolean;
}

function getActionIcon(entry: ConfirmEntry) {
  switch (entry.action) {
    case 'new':
    case 'extend':
    case 'recreate':
      return <CheckCircleOutlinedIcon sx={{ fontSize: '1rem', color: 'success.main' }} />;
    case 'override':
      return <AutorenewOutlinedIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />;
    case 'delete':
      return entry.detail ? (
        <AutorenewOutlinedIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
      ) : (
        <CheckCircleOutlinedIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
      );
    case 'ignored':
      return <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />;
    default:
      return null;
  }
}

function getActionTextColor(action: string) {
  if (action === 'ignored') return 'text.disabled';
  return 'text.primary';
}

export default function AvailabilityConfirmModal({
  open,
  onBack,
  onConfirm,
  summary,
  title,
  description,
  isApplying = false,
}: Props) {
  const statsByColor = useMemo(() => {
    const byColor: Record<'default' | 'warning' | 'success', Array<ConfirmStat>> = {
      default: [],
      warning: [],
      success: [],
    };
    summary.stats.forEach((stat) => {
      const color = stat.color ?? 'default';
      byColor[color].push(stat);
    });
    return byColor;
  }, [summary.stats]);

  return (
    <Dialog open={open} onClose={onBack} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <InfoOutlinedIcon color="info" />
          <Typography variant="h6">Confirm {title} Changes</Typography>
        </Stack>
      </DialogTitle>

      <Alert severity="info" sx={{ m: 2, mt: 0 }}>
        {description}
      </Alert>

      <DialogContent dividers>
        <Stack gap={3}>
          <Box>
            <Stack direction="row" gap={3} sx={{ mb: 2 }}>
              {statsByColor.default.map((stat) => (
                <Box key={stat.label}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              ))}
              {statsByColor.warning.map((stat) => (
                <Box key={stat.label}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              ))}
              {statsByColor.success.map((stat) => (
                <Box key={stat.label}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Stack gap={1.5}>
            {summary.rooms.map((room) => (
              <Box
                key={room.catalogRoomName}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {room.roomName} → {room.catalogRoomName}
                </Typography>

                {room.rates.map((rate) => {
                  const counts = rate.entries.reduce<Record<string, number>>((acc, e) => {
                    acc[e.action] = (acc[e.action] ?? 0) + 1;
                    return acc;
                  }, {});
                  return (
                    <Box key={rate.rateName} sx={{ mt: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {rate.rateName}
                        </Typography>

                        <Stack direction="row" gap={0.5}>
                          {!!counts['new'] && (
                            <Chip
                              size="small"
                              label={`${counts['new']} NEW`}
                              variant="filled"
                              sx={{ bgcolor: 'success.50', color: 'success.dark', fontSize: '0.7rem' }}
                            />
                          )}
                          {!!counts['override'] && (
                            <Chip
                              size="small"
                              label={`${counts['override']} OVERRIDE`}
                              variant="filled"
                              sx={{ bgcolor: 'warning.50', color: 'warning.dark', fontSize: '0.7rem' }}
                            />
                          )}
                          {!!counts['extend'] && (
                            <Chip
                              size="small"
                              label={`${counts['extend']} EXTEND`}
                              variant="filled"
                              sx={{ bgcolor: 'info.50', color: 'info.dark', fontSize: '0.7rem' }}
                            />
                          )}
                          {!!counts['delete'] && (
                            <Chip
                              size="small"
                              label={`${counts['delete']} DELETE`}
                              variant="filled"
                              sx={{ bgcolor: 'error.50', color: 'error.dark', fontSize: '0.7rem' }}
                            />
                          )}
                          {!!counts['recreate'] && (
                            <Chip
                              size="small"
                              label={`${counts['recreate']} RECREATE`}
                              variant="filled"
                              sx={{ bgcolor: 'success.50', color: 'success.dark', fontSize: '0.7rem' }}
                            />
                          )}
                          {!!counts['ignored'] && (
                            <Chip
                              size="small"
                              label={`${counts['ignored']} IGNORED`}
                              variant="outlined"
                              sx={{ color: 'text.disabled', fontSize: '0.7rem' }}
                            />
                          )}
                        </Stack>
                      </Stack>

                      {rate.entries.map((entry, i) => (
                        <Stack
                          key={`${entry.start}-${entry.end}-${entry.action}-${i}`}
                          direction="row"
                          alignItems="center"
                          gap={1}
                          sx={{ pl: 1, py: 0.25 }}
                        >
                          {getActionIcon(entry)}
                          <Typography variant="body2" sx={{ color: getActionTextColor(entry.action) }}>
                            {entry.start} – {entry.end}
                          </Typography>
                          {entry.detail && (
                            <Typography variant="caption" color="text.secondary">
                              {entry.detail}
                            </Typography>
                          )}
                        </Stack>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          This action cannot be undone.
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="text" startIcon={<RestartAltOutlinedIcon />} onClick={onBack} disabled={isApplying}>
            Back to review
          </Button>
          <Button variant="contained" onClick={onConfirm} disabled={isApplying}>
            {isApplying ? 'Applying...' : 'Confirm & Apply'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
